/**
 * routeMap.js — canonical production route patterns and route builders.
 *
 * Single source of truth for all canonical admin app routes.
 *
 * ── SEPARATION CONTRACT ────────────────────────────────────────────────────
 *
 *   `routeShopId`      — the URL path segment derived from the browser route.
 *                        Used ONLY for navigation; never used as a gateway
 *                        token, session credential, or auth identifier.
 *
 *   `gatewayToken`     — an HTTP authentication credential managed by the
 *                        session layer (gateway JWT). Must never appear in a
 *                        route path or be passed to a route builder.
 *
 *   `prestashopShopId` — the platform shop identifier returned by the bridge
 *                        bootstrap (`installation.internalShopId`). A separate
 *                        namespace; never the same as `routeShopId`.
 *
 * These three identifiers are defined by the shell auth contract and MUST
 * remain semantically independent across the codebase.
 *
 * ──────────────────────────────────────────────────────────────────────────
 */

// ── Canonical route patterns (React Router path strings) ─────────────────────
//
// `:routeShopId` is the named URL segment for the current shop context.
// The name is intentionally distinct from `token`, `gatewayToken`, `shopId`,
// and `internalShopId` to prevent accidental conflation at the call site.

/**
 * @type {Record<string, string>}
 */
export const ROUTE_PATTERNS = {
  // ── Auth shell surface ───────────────────────────────────────────────────
  /** Sign-in / password-recovery surface. Full-screen, no app nav. */
  signin: '/signin',

  // ── Wizard shell surface ─────────────────────────────────────────────────
  /** Full-screen setup wizard. Rendered by WizardShell (NO top nav). */
  setup: '/app/:routeShopId/setup',

  /** Search-interface wizard. Cloud keeps this separate from data onboarding. */
  searchSetup: '/app/:routeShopId/search/setup',

  // ── Normal app shell surface ─────────────────────────────────────────────
  /** Normal app entry point. Rendered inside AppLayout (top nav present). */
  home: '/app/:routeShopId/home',

  /** Diagnostics page. Rendered inside AppLayout. */
  diagnostics: '/app/:routeShopId/diagnostics',

  /**
   * Search settings page with named tab selector.
   * `:tab` is a UI routing param (e.g. 'theme', 'data').
   * Rendered inside AppLayout via SearchRouter.
   */
  searchSettings: '/app/:routeShopId/search/settings/:tab',

  /** Search fields customization page. Rendered inside AppLayout via SearchRouter. */
  searchFields: '/app/:routeShopId/search/customization/fields',

  /** Search filters customization page. Rendered inside AppLayout via SearchRouter. */
  searchFilters: '/app/:routeShopId/search/customization/filters',

  /** Search synonyms customization page. Rendered inside AppLayout via SearchRouter. */
  searchSynonyms: '/app/:routeShopId/search/customization/synonyms',

  /** Cloud exact-query controls for term and word suggestions. */
  searchSuggestionOverrides: '/app/:routeShopId/search/customization/suggestion-overrides',

  /** Cloud query-to-taxonomy mapping and taxonomy source configuration. */
  searchTaxonomy: '/app/:routeShopId/search/customization/taxonomy',

  /** Merchandising rule workspace and its production sub-routes. */
  merchandisingRules: '/app/:routeShopId/search/merchandising/rules',
  merchandisingCategories: '/app/:routeShopId/search/merchandising/rules/categories',
  merchandisingQueryRules: '/app/:routeShopId/search/merchandising/rules/query',
  merchandisingProductRanking: '/app/:routeShopId/search/merchandising/rules/products',
  merchandisingNewRule: '/app/:routeShopId/search/merchandising/rules/new',
  merchandisingRuleDetail: '/app/:routeShopId/search/merchandising/rules/:ruleId',

  // ── Entry mounts (used for React Router <Route path=...> declarations) ────
  /** Top-level normal-app mount. All /app/... routes go through AppLayout. */
  app: '/app',

  // ── Embedded/dev surface ────────────────────────────────────────────────
  /** Embedded subtree — handled by the Embedded component (includes /embedded/dev dev-only). */
  embedded: '/embedded',
};

// ── Route builders ────────────────────────────────────────────────────────────
//
// Each builder accepts `routeShopId` as the first argument and produces a
// fully-qualified path string suitable for history.push() / <Redirect to=>.
//
// RULE: `routeShopId` is the URL navigation segment ONLY.
//       Never pass a gateway token, session JWT, or PrestaShop shop ID here.

export const ROUTES = {
  /**
   * Auth/sign-in surface — full-screen, no app nav.
   * @returns {string}
   */
  signin: () => '/signin',

  /**
   * Home page — always rendered inside AppLayout.
   * @param {string} routeShopId
   * @returns {string}
   */
  home: (routeShopId) => `/app/${routeShopId}/home`,

  /**
   * Setup wizard — rendered by WizardShell, bypasses AppLayout top nav.
   * @param {string} routeShopId
   * @returns {string}
   */
  setup: (routeShopId) => `/app/${routeShopId}/setup`,

  /**
   * Search-interface setup wizard.
   * @param {string} routeShopId
   * @returns {string}
   */
  searchSetup: (routeShopId) => `/app/${routeShopId}/search/setup`,

  /**
   * Diagnostics page — rendered inside AppLayout.
   * @param {string} routeShopId
   * @returns {string}
   */
  diagnostics: (routeShopId) => `/app/${routeShopId}/diagnostics`,

  /**
   * Search settings page with tab.
   * @param {string} routeShopId
   * @param {string} [tab='theme'] — UI tab segment, not a token.
   * @returns {string}
   */
  searchSettings: (routeShopId, tab = 'theme') =>
    `/app/${routeShopId}/search/settings/${tab}`,

  /**
   * Search fields customization page.
   * @param {string} routeShopId
   * @returns {string}
   */
  searchFields: (routeShopId) =>
    `/app/${routeShopId}/search/customization/fields`,

  /**
   * Search filters customization page.
   * @param {string} routeShopId
   * @returns {string}
   */
  searchFilters: (routeShopId) =>
    `/app/${routeShopId}/search/customization/filters`,

  /**
   * Search synonyms customization page.
   * @param {string} routeShopId
   * @returns {string}
   */
  searchSynonyms: (routeShopId) =>
    `/app/${routeShopId}/search/customization/synonyms`,

  searchSuggestionOverrides: (routeShopId) =>
    `/app/${routeShopId}/search/customization/suggestion-overrides`,

  searchTaxonomy: (routeShopId) =>
    `/app/${routeShopId}/search/customization/taxonomy`,

  merchandisingRules: (routeShopId) =>
    `/app/${routeShopId}/search/merchandising/rules`,
  merchandisingCategories: (routeShopId) =>
    `/app/${routeShopId}/search/merchandising/rules/categories`,
  merchandisingQueryRules: (routeShopId) =>
    `/app/${routeShopId}/search/merchandising/rules/query`,
  merchandisingProductRanking: (routeShopId) =>
    `/app/${routeShopId}/search/merchandising/rules/products`,
  merchandisingNewRule: (routeShopId, type) =>
    `/app/${routeShopId}/search/merchandising/rules/new${type ? `?type=${encodeURIComponent(type)}` : ''}`,
  merchandisingRuleDetail: (routeShopId, ruleId) =>
    `/app/${routeShopId}/search/merchandising/rules/${encodeURIComponent(ruleId)}`,
};
